import type { CampaignFull, LevelFull, LevelSummary, MeResponse, ScoreResponse, ScoreRow } from '../shared/api-types'
import type { GameModeNet } from '../shared/net-types'
import { levelFromAscii, validateLevel } from '../shared/level'
import { type CatalogEntry, isArenaLevel, isBonusLevel, TEST_LEVELS } from '../shared/levels-catalog'
import { autoDecor } from './auto-decor'
import { apiCampaignScore, apiGetLevel, apiLeaderboard, apiLibrary, apiLogout, apiMe, apiParcours, apiPlayLevel, apiSetSkin } from './api'
import { showChallengeToasts, showDuelToasts } from './economy-ui'
import { flushDrafts, flushScores, keepScore, registerServiceWorker, reloadIfUpdated, watchConnection } from './offline'
import { readLibrary, saveLibrary } from './maps-offline'
import { formatTime } from './format-time'
import { makePickerUi, type PickerEntry } from './level-picker-ui'
import { toggleMute, unlockAudio } from './audio'
import { resetAuthFields, showAuth } from './auth-ui'
import { startCampaigns } from './campaigns-ui'
import { startEditor } from './editor'
import { startHome } from './home-ui'
import { startLeaderboard } from './leaderboard-ui'
import { startMine } from './mine'
import { appliquerOverrides, chargerOverrides } from './overrides'
import { makeParcoursUi } from './parcours-ui'
import { startMyLevels } from './my-levels-ui'
import { startOnline } from './online-ui'
import { isTouch } from './platform'
import { equipped, loadLoadout } from './loadout'
import { ENTREE_CAMPAGNE, SLOT_ENTREE } from '../shared/entrees'
import { montreEntreeGagnee } from './entree-apercu'
import { drawMenuBackdrop } from './render'
import { drawCenterScreen, MenuHandle } from './screens'
import { dashPref, setAvatarHue, setSkinId, torchPref } from './prefs'
import { startSolo } from './solo'
import { loadDecorSheet, loadRunnersSheet, loadSheet, loadWorldTilesSheet, loadPetsSheet } from './sprites'

// Filet de debug (repris de Task 14) : toute erreur non rattrapée est PEINTE sur le
// canvas, pour qu'un écran noir muet devienne un message lisible (y compris headless).
function drawFatal(message: string): void {
  const canvas = document.getElementById('game') as HTMLCanvasElement | null
  const ctx = canvas?.getContext('2d')
  if (!ctx || !canvas) return
  ctx.fillStyle = '#0d0b1a'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#ff5c57'
  ctx.font = '24px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  message.split('\n').forEach((line, i) => ctx.fillText(line, 24, 24 + i * 30))
}

addEventListener('error', (e) => drawFatal('Erreur : ' + (e.message || String(e.error))))
addEventListener('unhandledrejection', (e) => drawFatal('Promesse rejetée : ' + String(e.reason)))

// Crochet de dev : ?level=N saute le menu et lance ce niveau (utile pour les captures
// headless, sans effet en usage normal). Renvoie -1 si absent/invalide.
function parseDevLevel(): number {
  const m = /[?&]level=(\d+)/.exec(location.search)
  if (!m) return -1
  const n = Number(m[1])
  return Number.isInteger(n) && n >= 0 && n < TEST_LEVELS.length ? n : -1
}

// Plein écran au premier geste, tenté UNE fois. Marche sur Android/iPad (API Fullscreen) ;
// sur iPhone Safari en onglet l'API n'existe pas -> échec silencieux (le vrai plein écran y
// passe par « Sur l'écran d'accueil », rappelé par maybeShowInstallHint).
let fsTried = false
function enterFullscreenOnce(): void {
  if (fsTried) return
  fsTried = true
  const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }
  const req = el.requestFullscreen ?? el.webkitRequestFullscreen
  if (req && !document.fullscreenElement) { try { void req.call(el) } catch { /* non supporté (iPhone Safari) */ } }
}

// iPhone Safari en onglet : aucun moyen de masquer la barre du navigateur. On invite une
// fois à installer sur l'écran d'accueil (vrai plein écran). Masqué si déjà en app installée
// (standalone), si l'API Fullscreen existe (Android/iPad la gèrent), ou si déjà rejeté.
function maybeShowInstallHint(): void {
  const standalone = matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true
  const canFullscreen = typeof document.documentElement.requestFullscreen === 'function'
  if (standalone || canFullscreen || localStorage.getItem('mad_hint_install') === '1') return
  const hint = document.getElementById('install-hint')
  if (!hint) return
  hint.hidden = false
  hint.querySelector('#install-hint-close')?.addEventListener('click', () => {
    hint.hidden = true
    localStorage.setItem('mad_hint_install', '1')
  })
}

const main = async (): Promise<void> => {
  // Appareil tactile : on pose la classe qui active le CSS mobile (overlay portrait,
  // touch-action sur le canvas). isTouch = constante de session, pas de bascule dynamique.
  if (isTouch) document.documentElement.classList.add('is-touch')
  if (isTouch) {
    // Safari iOS IGNORE user-scalable=no : on bloque explicitement le zoom par pincement
    // (gesturestart) et par double-tap (dblclick, sauf dans un champ de saisie où le double-tap
    // sélectionne un mot). touch-action:manipulation (CSS) neutralise le reste.
    addEventListener('gesturestart', (e) => e.preventDefault())
    addEventListener('dblclick', (e) => {
      if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) e.preventDefault()
    })
    maybeShowInstallHint()
  }
  const [sheet] = await Promise.all([loadSheet(), loadDecorSheet(), loadRunnersSheet(), loadWorldTilesSheet(), loadPetsSheet()]) // + décors (18/07) + coureurs cartoon (19/07) + tuiles HD (20/07) + compagnons HD (21/07)
  const canvas = document.getElementById('game') as HTMLCanvasElement
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = false
  const devLevel = parseDevLevel()

  // Son : premier keydown = geste utilisateur -> on débloque l'AudioContext ; la touche
  // M bascule le muet au niveau du document (actif au menu comme en jeu). Persistance
  // dans localStorage via toggleMute ; le HUD reflète l'état pendant une partie.
  let audioUnlocked = false
  addEventListener('keydown', (e) => {
    if (!audioUnlocked) { audioUnlocked = true; unlockAudio() }
    // Quand on tape dans un champ (overlay de connexion), les touches appartiennent au
    // formulaire : « m » doit s'écrire dans le pseudo, pas couper le son.
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
    if (e.code === 'KeyM') { e.preventDefault(); toggleMute() }
  })
  // Pendant tactile du keydown : le premier toucher débloque aussi l'AudioContext (iOS ne
  // réveille le son que dans un geste utilisateur). Même variable audioUnlocked, unlockAudio
  // idempotent : on reste symétrique du keydown.
  addEventListener('pointerdown', () => {
    if (!audioUnlocked) { audioUnlocked = true; unlockAudio() }
    if (isTouch) enterFullscreenOnce() // 1er toucher = geste utilisateur : plein écran là où c'est permis
  }, { once: false })

  let menu: MenuHandle | null = null
  // Session courante (null = non connecté) et mode hors ligne (serveur injoignable :
  // le solo reste jouable, simplement les scores ne sont pas envoyés).
  let me: MeResponse | null = null
  let offline = false

  function clearMenu(): void {
    menu?.destroy(); menu = null
  }
  // Ce qu'il y a à fêter au retour d'un envoi de score : défis de carte accomplis (22/07) et duels
  // différés remportés (25/07). Passé aussi à la file hors ligne, pour qu'un temps fait au chalet
  // déclenche sa célébration au retour du réseau, des jours plus tard s'il le faut.
  const fete = (res: ScoreResponse): void => { showChallengeToasts(res.challengesCompleted); showDuelToasts(res.duelsWon) }
  // Envoi d'un score « tirer et oublier » : n'attend jamais, ne bloque jamais le jeu.
  // Un seul réessai à ~2 s absorbe un hoquet réseau ; au-delà on renonce en silence
  // (un score n'est pas vital, et une victoire n'est jamais retardée par le réseau).
  // `mode` = dash/torche de la partie jouée : le serveur tient un record PAR combinaison.
  // DÉFIS DE CARTES (22/07) : si ce run en accomplit un pour la 1re fois, le serveur le
  // renvoie (sinon []) -> toast de célébration, y compris sur le réessai.
  function submitScore(slug: string, timeMs: number, deaths: number, mode: { dash: boolean; torch: boolean }): void {
    // MODE HORS LIGNE (25/07) : le temps est d'abord RANGÉ dans la file locale, puis la file est
    // vidée. En ligne, ça revient exactement à l'envoi d'avant (un aller-retour, puis la file est
    // vide) ; sans réseau, le temps attend le retour au lieu de disparaître. Le vidage absorbe aussi
    // le hoquet réseau que l'ancien réessai à 2 secondes couvrait, et sans le limiter à un essai.
    keepScore(slug, timeMs, deaths, mode)
    void flushScores(fete)
  }
  // Niveaux de base + couche décor cosmétique AUTO (20/07, demande Claude-Alain « ajoute des déco aux
  // niveaux de base »). Le decor est HORS simulation -> n'affecte NI le terrain NI les CHRONOS/RECORDS.
  // Généré au RUNTIME (le catalogue et les .txt restent inchangés -> test de synchro vert). Un niveau
  // ayant déjà un decor est préservé tel quel.
  // ⚠️ On part du catalogue SURCHARGÉ (client/overrides) : une carte officielle corrigée par
  // l'admin doit être celle que TOUT LE MONDE joue, sinon la famille compare des chronos posés
  // sur deux terrains différents.
  //
  // ⚠️ RECALCULÉ après le chargement des surcharges, et pas seulement au démarrage : ce bloc
  // s'exécute AVANT l'appel réseau. Figé ici, il servirait éternellement la version d'origine et
  // la correction de l'admin ne serait visible de personne — y compris de lui.
  const construireNiveaux = (): CatalogEntry[] =>
    appliquerOverrides(TEST_LEVELS).map((l) => (l.decor ? l : { ...l, decor: autoDecor(l.rows, l.world) }))
  let TEST_LEVELS_HD = construireNiveaux()
  function startGame(startIndex: number): void {
    clearMenu()
    startSolo({
      canvas, sheet, levels: TEST_LEVELS_HD, startIndex,
      // L'entrée en scène GAGNÉE se porte comme un chapeau : partout où l'on joue seul. La
      // campagne, elle, impose la sienne (voir playCampaignSolo).
      entree: equipped(SLOT_ENTREE) ?? undefined,
      // Free-play (20/07) : niveaux de test = terrain de speed-run. À la victoire l'écran attend le
      // choix (R/🔄 rejouer le même, →/tap suivant) au lieu d'enchaîner tout seul. Les CAMPAGNES
      // gardent l'enchaînement auto (elles ne passent pas ce flag -> défaut true dans startSolo).
      autoAdvance: false,
      onQuit: () => showMainMenu(),
      // Envoyé uniquement si connecté (me non null) : hors ligne ou déconnecté = pas d'envoi.
      onLevelWon: (slug, timeMs, deaths, mode) => { if (me || offline) submitScore(slug, timeMs, deaths, mode) },
    })
  }
  /** Lance un niveau du PARCOURS par son slug. Retombe sur le premier niveau si le slug a disparu
   *  du catalogue (ne devrait pas arriver : un test refuse un nœud qui cite un slug inconnu). */
  function startGameSlug(slug: string): void {
    const i = TEST_LEVELS_HD.findIndex((l) => l.slug === slug)
    startGame(i >= 0 ? i : 0)
  }

  /**
   * LE PARCOURS (30/07) : la carte de progression, écran d'entrée de « Jouer ».
   *
   * ⚠️ Réservé au joueur CONNECTÉ, et c'est délibéré : une carte de progression sans progression
   * enregistrée serait un mensonge (tout paraîtrait verrouillé à chaque partie). Hors ligne ou
   * déconnecté, « Jouer » garde son comportement d'avant — la liste libre, dès le premier niveau.
   */
  function showParcours(): void {
    clearMenu()
    const ui = makeParcoursUi(
      (slug) => { ui.destroy(); startGameSlug(slug) },
      () => { ui.destroy(); showMainMenu() },
    )
    menu = ui
    apiParcours()
      .then((dto) => ui.update(dto))
      .catch(() => { ui.destroy(); startGame(0) })   // API muette : on ne bloque jamais le jeu
  }

  function showLeaderboard(): void {
    clearMenu()
    startLeaderboard(canvas, sheet, () => showMainMenu())
  }
  // « La Mine aux briques » (Lot C) : la scène jouable (marche + socles + boutique + révélation).
  // Câblée pour tous (la scène marche hors ligne ; les actions réseau dégradent en toast si non
  // connecté). clearMenu() AVANT startMine, comme startGame/showLeaderboard (sinon l'accueil DOM reste).
  function showMine(): void {
    clearMenu()
    startMine({ canvas, sheet, onQuit: () => showMainMenu() })
  }
  // Accueil PREMIUM (refonte 16/07) : overlay DOM (home-ui) — fond de biomes animé, tuiles
  // d'action, Réglages intégrés. « En ligne / Campagnes / Créer / Se déconnecter » n'existent
  // QUE connecté (me non null) : créer et publier une carte exige une session. Hors ligne
  // (serveur injoignable) : jeu solo seul, footer l'annonce.
  function showMainMenu(): void {
    // Si une nouvelle version du jeu a pris la main pendant la session, on recharge ICI : jamais au
    // milieu d'une course. Sans ce rappel, on jouerait l'ancien bundle jusqu'à la fermeture.
    reloadIfUpdated()
    clearMenu()
    menu = startHome({
      sheet, me, offline,
      actions: {
        // « Jouer » ouvre la CARTE quand on est connecté, la liste libre sinon (cf. showParcours).
        onPlay: () => (me ? showParcours() : startGame(0)),
        onPick: () => showLevelPicker(),
        onLeaderboard: () => showLeaderboard(),
        onMine: () => showMine(),
        // CRÉER : disponible AUSSI hors ligne (25/07, demande Claude-Alain). L'éditeur ne dépend
        // pas du réseau : la carte est rangée sur l'appareil et publiée au retour. Sans cette
        // exception, la tuile disparaissait exactement dans la situation où elle sert le plus
        // (le trajet, le chalet), et la fonction était inatteignable.
        ...(me || offline ? { onMyLevels: () => showMyLevels() } : {}),
        // En ligne, campagnes et déconnexion exigent un vrai échange avec le serveur : les
        // proposer hors ligne ne mènerait qu'à un écran d'erreur.
        ...(me ? {
          onOnline: () => showOnline(),
          onCampaigns: () => showCampaigns(),
          onLogout: () => logout(),
        } : {}),
      },
    })
  }
  // « Cartes » (refonte 16/07) : overlay DOM avec MOTEUR DE RECHERCHE + grille de vignettes à
  // défilement natif (level-picker-ui). Niveaux de test d'abord, puis la bibliothèque famille
  // (cartes publiées de tous) et les records de TA config actuelle, chargés ensuite : chaque
  // arrivée rafraîchit la grille via update() SANS perdre la recherche tapée. Hors ligne / non
  // connecté / erreur : seuls les niveaux de test restent (les requêtes rejettent en silence).
  function showLevelPicker(): void {
    clearMenu()
    let active = true // faux dès qu'on quitte l'écran : bloque un refresh tardif de la bibliothèque
    let library: LevelFull[] = []
    // Meilleur temps PAR NIVEAU pour la config de jeu ACTUELLE (dash/torche des réglages),
    // slug -> meilleure ligne de cette variante (le classement arrive trié par temps).
    let records = new Map<string, ScoreRow>()
    const leave = (fn: () => void): void => { active = false; fn() }
    function pick(i: number): void {
      if (i < TEST_LEVELS.length) { leave(() => startGame(i)); return }
      const lvl = library[i - TEST_LEVELS.length]
      if (lvl) leave(() => startFamilyLevel(lvl))
    }
    const picker = makePickerUi(sheet, pick, () => leave(() => showMainMenu()))
    menu = picker
    function refresh(): void {
      const rec = (slug: string): string | undefined => {
        const r = records.get(slug)
        return r ? `⏱ ${formatTime(r.bestMs)} ${r.pseudo}` : undefined
      }
      // Numérotation du PARCOURS (25/07) : seules les étapes du parcours portent un numéro. Les
      // GRANDES CARTES (08 et 11, rangées en fin par PARCOURS_ORDER) sont annoncées pour ce
      // qu'elles sont — de l'exploration libre — pour qu'aucun joueur ne les prenne pour le palier
      // suivant et n'y bute. Elles restent parfaitement jouables : rien n'est retiré du jeu.
      // Les ARÈNES du versus (06/08) sortent de la numérotation pour la même raison que les
      // grandes cartes, mais elles ne se présentent pas pareil : une arène ne se « finit » pas,
      // elle se gagne à deux ou à trois. Les annoncer comme telles évite qu'un joueur seul les
      // prenne pour l'étape suivante et se demande ce qu'il rate.
      let etape = 0
      const entries: PickerEntry[] = [
        ...TEST_LEVELS_HD.map((l) => {
          const arene = isArenaLevel(l.slug)
          const bonus = isBonusLevel(l.slug)
          if (!bonus && !arene) etape++
          return {
            key: l.slug, title: bonus || arene ? l.title : `${etape}. ${l.title}`,
            sub: arene ? 'Arène · duel à deux ou à trois' : bonus ? 'Grande carte · exploration libre' : undefined,
            record: rec(l.slug), rows: l.rows, world: l.world, decor: l.decor,
          }
        }),
        // Clé de vignette datée (updatedAt) : une carte modifiée rafraîchit sa miniature.
        ...library.map((l, i) => ({
          key: `fam-${l.id}-${l.updatedAt}`, title: `${TEST_LEVELS.length + i + 1}. ${l.name}`,
          // Sous-titre = auteur + compteur de parties (rend lisible le classement « les plus jouées d'abord »).
          sub: l.plays > 0 ? `par ${l.author} · ${l.plays} partie${l.plays > 1 ? 's' : ''}` : `par ${l.author}`,
          record: rec(`fam-${l.id}`), rows: l.ascii.split('\n'), world: l.world, decor: l.decor,
        })),
      ]
      // Sous-titre = la config dont on affiche les records (celle des réglages actuels).
      picker.update(entries, `Records ${dashPref() ? 'Dash' : 'Classique'}${torchPref() ? ' + Torche' : ''}`)
    }
    refresh()
    // MIROIR DE LA BIBLIOTHÈQUE (25/07) : chaque chargement réussi est rangé sur l'appareil, et
    // c'est cette copie qu'on joue sans réseau. Elle est donc au plus vieille de la dernière
    // connexion. ⚠️ On ne passe PAS par le cache du service worker (règle « rien de /api/ en
    // cache ») : c'est l'application qui garde une copie, délibérément, de cette seule route.
    apiLibrary()
      .then((libs) => {
        saveLibrary(localStorage, libs) // même si on a quitté l'écran : la copie sert la prochaine fois
        if (!active || libs.length === 0) return
        library = libs
        refresh()
      })
      .catch(() => {
        // Hors ligne : on joue la bibliothèque de la dernière connexion. Non connecté : elle est
        // vide, et on garde les niveaux de base seuls, exactement comme avant.
        const miroir = readLibrary(localStorage)
        if (!active || miroir.length === 0) return
        library = miroir
        refresh()
      })
    // Records de la config actuelle sur chaque carte. Échec réseau = silence
    // (le sélecteur reste 100 % fonctionnel sans records).
    apiLeaderboard()
      .then((lb) => {
        if (!active) return
        const d = dashPref(), t = torchPref()
        const m = new Map<string, ScoreRow>()
        for (const lvl of lb.levels) {
          const best = lvl.scores.find((s) => s.dash === d && s.torch === t)
          if (best) m.set(lvl.slug, best)
        }
        if (m.size > 0) { records = m; refresh() }
      })
      .catch(() => {})
  }
  // Joue une carte famille (bibliothèque) : on charge sa grille puis on lance le solo. Le
  // score n'est envoyé que si connecté (submitScore, tirer-et-oublier). Si la carte a été
  // supprimée/dépubliée entre l'affichage et le clic, on revient simplement à la liste.
  // FILET : toutes les cartes sont désormais visibles MÊME inachevées (le serveur n'exige
  // plus la jouabilité). On valide donc la carte AVANT de lancer ; si elle est injouable
  // (départ, or ou sortie manquants, ou format cassé), on affiche un écran non bloquant et
  // on revient au choix des niveaux au premier geste, SANS démarrer de partie (jamais de crash).
  function startFamilyLevel(lvl: LevelSummary): void {
    clearMenu()
    drawCenterScreen(ctx, 'Chargement...', lvl.name)
    // HORS LIGNE (25/07) : on demande d'abord la version FRAÎCHE au serveur (une carte a pu être
    // retouchée depuis notre dernier passage), et sans réseau on retombe sur la copie du miroir —
    // que `library` porte déjà, puisque la bibliothèque arrive avec sa grille. Sans ce repli, une
    // carte de la famille resterait injouable en voiture alors qu'on l'a sur l'appareil.
    const copieLocale = (lvl as Partial<LevelFull>).ascii ? (lvl as LevelFull) : null
    apiGetLevel(lvl.id)
      .catch((e) => { if (copieLocale) return copieLocale; throw e })
      .then((full) => {
        const rows = full.ascii.split('\n')
        let problems: string[]
        try {
          problems = validateLevel(levelFromAscii(full.name, rows))
        } catch (e) {
          problems = [e instanceof Error ? e.message : String(e)]
        }
        if (problems.length > 0) {
          drawCenterScreen(ctx, 'Carte non jouable', "Il manque un départ, de l'or ou une sortie")
          // Écouteur one-shot (clavier OU clic) : au premier geste, on retire les DEUX
          // écouteurs et on revient au choix des niveaux. Aucun startSolo : pas de crash.
          // On ferme sur `click` (pas `pointerdown`) : sinon, au tactile, le `click` du MÊME
          // tap serait reçu par le menu que showLevelPicker vient de recréer et lancerait une
          // entrée par mégarde (le reste de l'appli sélectionne aussi sur `click`).
          const back = (): void => {
            removeEventListener('keydown', back)
            removeEventListener('click', back)
            showLevelPicker()
          }
          addEventListener('keydown', back, { once: true })
          addEventListener('click', back, { once: true })
          return
        }
        void apiPlayLevel(full.id) // compte cette partie (classement « les plus jouées ») — fire-and-forget
        startSolo({
          canvas, sheet,
          levels: [{ slug: `fam-${full.id}`, title: full.name, rows, world: full.world, monkSpeed: full.monkSpeed, smartMonks: full.smartMonks, monkPowers: full.monkPowers, decor: full.decor }],
          startIndex: 0,
          entree: equipped(SLOT_ENTREE) ?? undefined,
          onQuit: () => showLevelPicker(),
          onLevelWon: (slug, timeMs, deaths, mode) => { if (me || offline) submitScore(slug, timeMs, deaths, mode) },
        })
      })
      .catch(() => showLevelPicker())
  }
  // « Créer / Mes niveaux » : overlay DOM des cartes du joueur (Task 7). « Nouvelle carte »
  // ou « Modifier » ouvre l'éditeur ; à sa sortie on rouvre « Mes niveaux » ; Échap revient
  // au menu principal. L'overlay se masque lui-même avant d'ouvrir l'éditeur (tous deux
  // plein écran) ; startEditor reçoit initial=undefined pour une carte vierge.
  function showMyLevels(): void {
    clearMenu()
    startMyLevels({
      canvas, sheet,
      role: me?.role,
      // CARTE OFFICIELLE (30/07, admin) : on l'ouvre depuis le catalogue DÉJÀ SURCHARGÉ, sinon
      // Claude-Alain repartirait de la version d'origine et écraserait sa propre correction
      // précédente sans s'en apercevoir.
      onEditOfficial: (slug) => {
        const l = appliquerOverrides(TEST_LEVELS).find((x) => x.slug === slug)
        if (!l) return
        startEditor({
          canvas, sheet, onQuit: () => showMyLevels(),
          initial: {
            officielSlug: slug, name: l.title, rows: l.rows, world: l.world,
            monkSpeed: l.monkSpeed ?? 'normal', smartMonks: l.smartMonks ?? false,
            monkPowers: l.monkPowers ?? false, decor: l.decor ?? '',
          },
        })
      },
      onEdit: (lvl) => startEditor({ canvas, sheet, initial: lvl, onQuit: () => showMyLevels() }),
      // Jouer une carte créée hors ligne : pas de `onLevelWon`, donc AUCUN temps envoyé. Elle
      // n'existe pas encore côté serveur ; un score n'aurait rien à quoi se rattacher, et la file
      // le rejetterait au retour du réseau.
      onPlayDraft: (d) => startSolo({
        canvas, sheet,
        levels: [{ slug: 'brouillon', title: d.name, rows: d.rows, world: d.world, monkSpeed: d.monkSpeed, smartMonks: d.smartMonks, monkPowers: d.monkPowers, decor: d.decor }],
        startIndex: 0,
        entree: equipped(SLOT_ENTREE) ?? undefined,
        onQuit: () => showMyLevels(),
      }),
      onQuit: () => showMainMenu(),
    })
  }
  // « Jouer en ligne » (lot 4b) : overlay DOM du lobby multijoueur (présence, salons,
  // création / rejoindre). La socket /ws s'authentifie par le cookie de session, d'où
  // l'entrée réservée au connecté. À la sortie (Retour, Échap, perte de connexion) on
  // rouvre le menu principal. La partie en ligne (startOnlineGame) démarre depuis cet écran
  // dès qu'un salon devient complet, et une déconnexion en partie y ramène pour reprendre.
  // autoCreate : quand on arrive depuis une campagne à 2 (coop OU duel), on ouvre le lobby en
  // mode auto-création d'un salon campagne dans CE mode (voir playCampaignCoop/Versus). Absent
  // = lobby normal.
  function showOnline(autoCreate?: { campaignId: number; name: string; mode: GameModeNet; players?: number }): void {
    clearMenu()
    startOnline({ canvas, sheet, onQuit: () => showMainMenu(), autoCreate })
  }
  // « Campagnes » : overlays DOM (liste + créateur). onPlaySolo lance en solo, onPlayCoop en
  // coop en ligne, onPlayVersus en duel en ligne. Quand un callback est fourni, campaigns-ui
  // fait teardown() de l'overlay PUIS l'appelle. Retour / Échap = menu principal.
  function showCampaigns(): void {
    clearMenu()
    startCampaigns({
      canvas, sheet, me,
      onQuit: () => showMainMenu(),
      onPlaySolo: (full) => playCampaignSolo(full),
      onPlayCoop: (full, players) => playCampaignCoop(full, players),
      onPlayVersus: (full, players) => playCampaignVersus(full, players),
    })
  }
  // Lance une campagne en SOLO (Task 4). campaigns-ui a déjà fait teardown() de son overlay avant
  // de nous appeler : le canvas est libre. FILET : si un niveau de la campagne ne résout plus
  // (carte famille supprimée depuis la composition → rows null renvoyé par le serveur), on n'entre
  // PAS en jeu ; on peint un écran non bloquant et on revient aux campagnes au premier geste (même
  // filet que startFamilyLevel pour une carte injouable, jamais de startSolo ni de crash).
  function playCampaignSolo(full: CampaignFull): void {
    if (full.levels.some((l) => l.rows === null)) {
      drawCenterScreen(ctx, 'Campagne indisponible', 'Un niveau a été supprimé')
      // Écouteur one-shot (clavier OU clic) : au premier geste on retire les DEUX écouteurs et on
      // revient à l'écran Campagnes. On ferme sur `click` (pas `pointerdown`) : sinon, au tactile,
      // le `click` du MÊME tap serait reçu par l'overlay que showCampaigns recrée et lancerait une
      // action par mégarde (le reste de l'appli sélectionne aussi sur `click`).
      const back = (): void => {
        removeEventListener('keydown', back)
        removeEventListener('click', back)
        showCampaigns()
      }
      addEventListener('keydown', back, { once: true })
      addEventListener('click', back, { once: true })
      return
    }
    // Tous les niveaux résolvent (rows! sûr après le garde ci-dessus). startSolo gère 5 vies
    // GLOBALES sur la campagne entière et l'écran final « BRAVO » ; onQuit (Échap / game over)
    // ramène aux campagnes.
    const levels = full.levels.map((l) => ({ slug: l.slug, title: l.title, rows: l.rows!, world: l.world, monkSpeed: l.monkSpeed, smartMonks: l.smartMonks, monkPowers: l.monkPowers, decor: l.decor }))
    // Temps de bout en bout : on cumule le temps figé de chaque niveau réussi et on n'envoie le
    // total au classement QUE quand la campagne est bouclée (dernier niveau gagné) ET qu'on est
    // connecté (me non null, comme submitScore). Une campagne perdue avant (game over global)
    // ne remonte rien. apiCampaignScore est tirer-et-oublier : le .catch absorbe un échec réseau
    // et évite une promesse rejetée non gérée (qui peindrait un écran fatal).
    let total = 0
    let wins = 0
    startSolo({
      canvas, sheet, levels, startIndex: 0,
      // ENTRÉE EN SCÈNE (01/08) : toute campagne ouvre sur celle du mode campagne, pour tout le
      // monde et sans rien avoir gagné (décision Claude-Alain). Ce qu'on a gagné se porte AILLEURS,
      // en solo libre — d'où le fait que l'entrée équipée ne soit pas lue ici.
      entree: ENTREE_CAMPAGNE,
      onQuit: () => showCampaigns(),
      onLevelWon: (_slug, timeMs) => {
        total += timeMs
        wins += 1
        if (wins >= levels.length && me) {
          apiCampaignScore(full.id, total)
            .then((r) => { if (r.entreeGagnee) montreEntreeGagnee(r.entreeGagnee, full.name) })
            .catch(() => {})
        }
      },
    })
  }
  // Lance une campagne en COOP en ligne (Task 6). campaigns-ui a déjà fait teardown() de son
  // overlay avant de nous appeler. On ouvre « Jouer en ligne » en mode auto-création : dès
  // l'entrée au lobby, online-ui envoie create_room { campaignId, mode: 'coop', monks: false } ;
  // le serveur résout les 5 niveaux (refus propre affiché dans le lobby si l'un manque) et
  // enchaîne les niveaux à chaque victoire coop. Aucun garde rows null ici (contrairement au
  // solo) : c'est le serveur qui revalide la campagne à la création. Retour / Échap / fin de
  // partie = menu principal (via le onQuit de showOnline).
  function playCampaignCoop(full: CampaignFull, players: number): void {
    showOnline({ campaignId: full.id, name: full.name, mode: 'coop', players })
  }
  // Lance une campagne en DUEL en ligne (étape 2). Même chemin que la coop, en mode versus :
  // online-ui auto-crée un salon campagne { campaignId, mode: 'versus' } ; le serveur enchaîne
  // les niveaux, compte les manches, et l'écran final annonce le champion. Le serveur revalide
  // la campagne à la création (refus propre affiché dans le lobby si un niveau manque).
  function playCampaignVersus(full: CampaignFull, players: number): void {
    showOnline({ campaignId: full.id, name: full.name, mode: 'versus', players })
  }
  // Titre peint sur le canvas DERRIÈRE l'overlay de connexion (l'overlay est semi-opaque
  // et centré : le titre reste visible en haut, l'esthétique du jeu transparaît). Depuis la
  // refonte accueil (16/07) : fond de biome nuit étoilée + voile + titre au dégradé doré,
  // pour que l'écran de connexion soit de la même famille que l'accueil premium.
  function drawTitleBackdrop(): void {
    const W = canvas.width, H = canvas.height
    ctx.fillStyle = '#0d0b1a'; ctx.fillRect(0, 0, W, H)
    drawMenuBackdrop({ ctx, sheet }, 'classique', 0) // un instant figé du ciel étoilé (1344x768)
    ctx.fillStyle = 'rgba(13, 11, 26, 0.55)'; ctx.fillRect(0, 0, W, H)
    const gold = ctx.createLinearGradient(0, H * 0.16 - 38, 0, H * 0.16 + 38)
    gold.addColorStop(0, '#fff3b0'); gold.addColorStop(0.5, '#ffd23f'); gold.addColorStop(1, '#e0940f')
    ctx.fillStyle = gold
    ctx.font = 'bold 76px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)'; ctx.shadowBlur = 18; ctx.shadowOffsetY = 6
    ctx.fillText("MAD'TABORNIO", W / 2, H * 0.16)
    ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0
  }
  // Couleur VERROUILLÉE en ocre (héros unique « Le Baroudeur », sélecteur de couleur retiré le
  // 19/07). Des teintes choisies AVANT ce retrait restent sur le COMPTE (source de vérité, réécrit
  // le localStorage à chaque boot) et « bloquaient » le perso (ex. rose) sans moyen d'en sortir.
  // On force donc l'ocre au rendu ET, une seule fois par compte encore teinté, on remet le compte
  // à l'ocre (best-effort). À retirer si un sélecteur de couleur revient (voir openSkinPicker, dormant).
  const lockOchre = (accountHue: number): void => {
    setAvatarHue(-1)
    if (accountHue !== -1) void apiSetSkin('standard', -1)
  }
  // Ouvre l'overlay de connexion/inscription ; au succès on garde la session et on
  // bascule sur le menu connecté (« Bonjour {pseudo} »).
  function openAuth(): void {
    clearMenu()
    drawTitleBackdrop()
    showAuth((session) => { me = session; setSkinId(session.skin); lockOchre(session.skinHue); void loadLoadout(); showMainMenu() })
  }
  // « Se déconnecter » : ferme la session côté serveur (sans bloquer si l'appel échoue),
  // puis repropose l'écran de connexion.
  function logout(): void {
    clearMenu()
    resetAuthFields() // Mac familial partagé : ne pas laisser les identifiants du compte précédent
    apiLogout().catch(() => {}).finally(() => { me = null; openAuth() })
  }

  // Crochets de dev (captures headless) : court-circuitent l'authentification.
  // ⚠️ `?level=N` ATTEND l'équipement avant de lancer. Sans cette attente, `equipped()` répond
  // vide au démarrage (le chargement du loadout, plus bas, n'a pas encore eu lieu) et la capture
  // montre un héros sans tenue, sans chapeau et SANS ENTRÉE EN SCÈNE — ce qui a bien failli me
  // faire conclure que l'animation ne se déclenchait pas. `.finally` : hors ligne ou non connecté,
  // le niveau se lance quand même, exactement comme avant.
  if (devLevel >= 0) { loadLoadout().finally(() => startGame(devLevel)); return }
  // ?entree-gagnee=entree-cage : montre la carte de déblocage sans avoir à finir une campagne.
  // Elle n'apparaît qu'une fois dans la vie d'un joueur pour une entrée donnée ; sans ce crochet,
  // elle serait le seul écran du jeu impossible à revoir, donc impossible à vérifier.
  {
    const m = /[?&]entree-gagnee=([a-z-]+)/.exec(location.search)
    if (m) montreEntreeGagnee(m[1]!, 'Nuit de la mine')
  }
  if (/[?&]picker\b/.test(location.search)) { showLevelPicker(); return }
  if (/[?&]leaderboard\b/.test(location.search)) { showLeaderboard(); return }
  // ?editor : ouvre directement l'éditeur (le menu « Mes niveaux » y mène en usage normal).
  if (/[?&]editor\b/.test(location.search)) { startEditor({ canvas, sheet, onQuit: () => showMainMenu() }); return }
  // ?mylevels : ouvre directement l'écran « Mes niveaux » (captures headless ; la liste
  // exige une session, donc un cookie doit déjà être posé pour voir des cartes).
  if (/[?&]mylevels\b/.test(location.search)) { showMyLevels(); return }
  // ?online : ouvre directement l'écran « Jouer en ligne » (captures headless ; la socket
  // /ws exige une session, donc un cookie doit déjà être posé pour que la connexion aboutisse).
  if (/[?&]online\b/.test(location.search)) { showOnline(); return }
  // ?campaigns : ouvre directement l'écran « Campagnes » (captures headless ; la liste exige
  // une session, donc un cookie doit déjà être posé pour voir les campagnes). me reste null
  // ici (apiMe non appelé) : « Supprimer » sera masqué, sans effet sur la capture liste/créateur.
  if (/[?&]campaigns\b/.test(location.search)) { showCampaigns(); return }
  // ?mine : ouvre directement « La Mine aux briques » (Lot C ; captures headless / vérif visuelle).
  // La scène marche sans session ; wallet/boutique exigent un cookie posé (sinon dégradation silencieuse).
  if (/[?&]mine\b/.test(location.search)) { showMine(); return }

  // Boot normal : apiMe() renvoie la session, null si déconnecté (401), ou LÈVE si le
  // serveur est injoignable (dev:web, panne réseau) → mode hors ligne, solo jouable.
  try {
    me = await apiMe()
  } catch {
    offline = true
  }
  // Skins (19/07) : le COMPTE fait foi — on aligne les prefs locales sur la session pour que
  // le rendu (qui lit skinId()/avatarHue()) affiche le choix enregistré, sur tous les appareils.
  if (me) { setSkinId(me.skin); lockOchre(me.skinHue) }
  // Loadout (Lot C T5) : charge les cosmétiques équipés (best-effort) pour que les effets solo
  // (traînées / pas / victoire / titre) reflètent l'équipement dès le démarrage. Silencieux si non
  // connecté (le cache reste vide -> effets par défaut).
  if (me) void loadLoadout()
  // CARTES OFFICIELLES RETOUCHÉES (30/07) : on les charge AVANT le premier écran, puis on
  // reconstruit la liste jouée. Best-effort — hors ligne, on garde le catalogue embarqué.
  if (me) { await chargerOverrides(); TEST_LEVELS_HD = construireNiveaux() }
  // MODE HORS LIGNE (25/07) : le service worker rend la page ouvrable sans réseau ; le suivi de
  // connexion tient le bandeau à jour et vide la file dès que ça revient. Un vidage au démarrage
  // rattrape ce qu'une session précédente n'a pas pu envoyer (jeu fermé en mode avion).
  registerServiceWorker()
  watchConnection(fete)
  if (me) { void flushScores(fete); void flushDrafts() }
  if (me || offline) showMainMenu()
  else openAuth()
}

main().catch((e) => drawFatal('Échec du démarrage : ' + String(e)))
